import { darkenHex, hexToRgb, mixHex, normalizeHex } from '@/theme/colorUtils'

export type DerivedBanhangPalette = {
  primaryMain: string
  primaryDark: string
  primaryLight: string
  headerBg: string
  navItemActiveBg: string
  navItemActiveBgHover: string
  kiotSubmenuPanelBg: string
  invoiceTableHeaderBg: string
  tableLevel2HeadBg: string
  tableLevel2HeadBorder: string
  tableHeadBg: string
  tableHeadBorder: string
  /** Viền ngoài khối bảng (list). */
  tableWrapperBorder: string
  /** Dòng xen kẽ (zebra) — tint accent rất nhạt, nhạt hơn hover. */
  rowAltBg: string
  rowHoverBg: string
  rowSelectedBg: string
  rowSelectedHoverBg: string
  primarySurface: string
  primarySurfaceBorder: string
  primaryHoverRgba: string
}

/** Độ sáng tương đối 0..1 — dùng chỉnh tint bảng cho accent tối (navy/slate). */
function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex)
  if (!rgb) return 0.5
  const toLin = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * toLin(rgb.r) + 0.7152 * toLin(rgb.g) + 0.0722 * toLin(rgb.b)
}

export function deriveBanhangFromMain(mainRaw: string): DerivedBanhangPalette {
  const main = normalizeHex(mainRaw)
  const lum = relativeLuminance(main)
  /** Accent tối (navy/slate): giảm % trắng để tint còn nhìn thấy, hover rõ hơn. */
  const darkAccent = lum < 0.28

  const primaryDark = darkenHex(main, 0.14)
  const primaryLight = mixHex(main, '#ffffff', 0.38)
  /** Khớp KiotViet `.kv-nav-link.active` (~darken 19% từ `#0070f4` → `#005ac3`). */
  const navItemActiveBg = darkenHex(main, 0.19)
  const navItemActiveBgHover = darkenHex(main, 0.26)
  const kiotSubmenuPanelBg = navItemActiveBg

  const tableHeadBg = mixHex(main, '#ffffff', darkAccent ? 0.76 : 0.8)
  /** Viền thead theo accent (không giữ `#d6e4f2` xanh cố định). */
  const tableHeadBorder = mixHex(main, '#e2e8f0', darkAccent ? 0.45 : 0.52)
  /** Viền khối bảng — xám trung tính, pha accent rất nhẹ. */
  const tableWrapperBorder = mixHex(main, '#eaedf1', 0.88)
  /** Nền xanh lạnh rất nhạt pha chút accent — accent xám/slate pha trắng ra xám đục, nhìn bẩn. */
  const rowAltBg = mixHex(main, '#f6f9fd', 0.985)
  const rowHoverBg = mixHex(main, '#ffffff', darkAccent ? 0.93 : 0.955)
  const rowSelectedBg = mixHex(main, '#ffffff', darkAccent ? 0.82 : 0.86)
  const rowSelectedHoverBg = mixHex(main, '#ffffff', darkAccent ? 0.74 : 0.78)
  const invoiceTableHeaderBg = mixHex(main, '#ffffff', darkAccent ? 0.86 : 0.9)
  /** Bảng level 2 — nền header nhạt hơn level 1 (đồng bộ auth `level2HeadBg`). */
  const tableLevel2HeadBg = mixHex(main, '#ffffff', darkAccent ? 0.94 : 0.96)
  const tableLevel2HeadBorder = mixHex(main, '#dbeafe', darkAccent ? 0.42 : 0.48)
  const primarySurface = mixHex(main, '#ffffff', 0.92)
  const primarySurfaceBorder = mixHex(main, '#d0d0d0', 0.35)
  const px = hexToRgb(main)
  const primaryHoverRgba = px ? `rgba(${px.r}, ${px.g}, ${px.b}, 0.08)` : 'rgba(0, 112, 244, 0.08)'

  return {
    primaryMain: main,
    primaryDark,
    primaryLight,
    headerBg: main,
    navItemActiveBg,
    navItemActiveBgHover,
    kiotSubmenuPanelBg,
    invoiceTableHeaderBg,
    tableLevel2HeadBg,
    tableLevel2HeadBorder,
    tableHeadBg,
    tableHeadBorder,
    tableWrapperBorder,
    rowAltBg,
    rowHoverBg,
    rowSelectedBg,
    rowSelectedHoverBg,
    primarySurface,
    primarySurfaceBorder,
    primaryHoverRgba,
  }
}
